/**
 * Billing API client — thin wrappers around the ``/billing/*`` endpoints
 * that ``Subscription_Service`` exposes (backend R1).
 *
 * Every method returns the freshly-loaded ``BillingState``; callers feed
 * that back into the ``useBilling`` hook's cache so the UI updates in
 * one pass.
 */

import { request } from "../api";
import type { BillingState } from "./types";

export async function getBillingState(): Promise<BillingState> {
  return request<BillingState>("/billing/state", { method: "GET" });
}

export async function startTrial(args: {
  plan_id: string;
  payment_method_id?: string;
  card_brand?: string;
  card_last4?: string;
}): Promise<BillingState> {
  return request<BillingState>("/billing/start_trial", {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export async function startTrialNoCard(plan_id = "single"): Promise<BillingState> {
  return request<BillingState>("/billing/start_trial_no_card", {
    method: "POST",
    body: JSON.stringify({ plan_id }),
  });
}

export async function attachCard(args: {
  payment_method_id: string;
  card_brand?: string;
  card_last4?: string;
}): Promise<BillingState> {
  return request<BillingState>("/billing/attach_card", {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export async function startFree(): Promise<BillingState> {
  return request<BillingState>("/billing/start_free", { method: "POST" });
}

export async function requestConsent(): Promise<BillingState> {
  return request<BillingState>("/billing/request_consent", { method: "POST" });
}

export async function grantConsent(terms_version: number): Promise<BillingState> {
  return request<BillingState>("/billing/grant_consent", {
    method: "POST",
    body: JSON.stringify({ terms_version }),
  });
}

export async function revokeConsent(): Promise<BillingState> {
  return request<BillingState>("/billing/revoke_consent", { method: "POST" });
}

export async function subscribeNow(args: {
  plan_id: string;
  payment_method_id?: string;
  card_brand?: string;
  card_last4?: string;
}): Promise<BillingState> {
  return request<BillingState>("/billing/subscribe_now", {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export async function cancelSubscription(): Promise<BillingState> {
  return request<BillingState>("/billing/cancel", { method: "POST" });
}

export async function reactivateSubscription(): Promise<BillingState> {
  return request<BillingState>("/billing/reactivate", { method: "POST" });
}

/**
 * Change to a different plan. When the target plan has fewer creator seats
 * than the current family is using, ``kept_user_ids`` MUST contain exactly
 * the new seat count — any family member id (the account holder, a co-parent,
 * a kid). Members on a separate subscription are excluded by the caller.
 */
export async function changePlan(
  plan_id: string,
  kept_user_ids?: string[],
): Promise<BillingState> {
  const body: { plan_id: string; kept_user_ids?: string[] } = { plan_id };
  if (kept_user_ids !== undefined) body.kept_user_ids = kept_user_ids;
  return request<BillingState>("/billing/change_plan", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Choose who holds the plan's seats without changing the plan (seat unit,
 * 2026-10-22) — after a downgrade on Apple's sheet, or any time from Plan &
 * billing. Exactly `seats.count` family members; clears `choice_pending`.
 */
export async function setSeats(kept_user_ids: string[]): Promise<BillingState> {
  return request<BillingState>("/billing/seats", {
    method: "POST",
    body: JSON.stringify({ kept_user_ids }),
  });
}

export async function setSocialOptin(enabled: boolean): Promise<BillingState> {
  return request<BillingState>("/billing/social_optin", {
    method: "POST",
    body: JSON.stringify({ enabled }),
  });
}

export async function setDirectoryOptin(enabled: boolean): Promise<BillingState> {
  return request<BillingState>("/billing/directory_optin", {
    method: "POST",
    body: JSON.stringify({ enabled }),
  });
}

export async function createSetupIntent(): Promise<{ client_secret: string }> {
  return request<{ client_secret: string }>("/billing/stripe/setup_intent", {
    method: "POST",
  });
}

// ── App Store (ipad-native-shell 10.3) ──────────────────────────────────────

export interface AppleProductsConfig {
  /** false until the server's APPLE_IAP_* config is set; the app offers nothing. */
  enabled: boolean;
  products: { product_id: string; plan_id: string }[];
}

export async function getAppleProducts(): Promise<AppleProductsConfig> {
  return request<AppleProductsConfig>("/billing/apple/products", { method: "GET" });
}

/** Post StoreKit 2's ``jwsRepresentation``; returns the family's updated state. */
export async function postAppleTransaction(signed_transaction: string): Promise<BillingState> {
  return request<BillingState>("/billing/apple/transactions", {
    method: "POST",
    body: JSON.stringify({ signed_transaction }),
  });
}
